import {
	AnyType,
	jsonArrayProperty,
	jsonClass,
	jsonProperty,
} from '@sumbricht/json-class-serializer'
import type { OnlyData, OnlyWritableData } from '../types.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'
import {
	AuditTrailEntryAttachment,
	AuditTrailEntryAttachmentData,
} from './audit-trail-entry-attachment.js'
import type { UserIdAndName } from '../data/user-id-and-name.js'
import { ChatMessage } from '../chat/chat-message.js'

export const AuditTrailEntryTypes = ['message', 'action'] as const
export type AuditTrailEntryType = (typeof AuditTrailEntryTypes)[number]
export const AuditTrailActionTypes = {
	create: { name: 'Created', icon: 'add' },
	update: { name: 'Updated', icon: 'edit' },
	delete: { name: 'Deleted', icon: 'delete' },
	submit: { name: 'Submitted', icon: 'send' },
	resubmit: { name: 'Resubmitted', icon: 'send' },
	undelete: { name: 'Undeleted', icon: 'restore_from_trash' },
	review: { name: 'Reviewed', icon: 'visibility' },
	approve: { name: 'Approved', icon: 'verified' },
	reject: { name: 'Rejected', icon: 'thumb_down' },
	removeApproval: { name: 'Removed approval', icon: 'unpublished' },
	open: { name: 'Opened', icon: 'meeting_room' },
	close: { name: 'Closed', icon: 'door_front' },
	reopen: { name: 'Reopened', icon: 'meeting_room' },
	assign: { name: 'Assigned', icon: 'people_alt' },
	escalate: { name: 'Escalated', icon: 'new_releases' },
} as const
export type AuditTrailActionType =
	| keyof typeof AuditTrailActionTypes
	| (string & {})

@jsonClass('AuditTrailEntryData')
export class AuditTrailEntryData {
	// minimal data for creating an audit trail entry; MUST NOT include data that user should not be allowed to set
	@jsonProperty()
	relatedToType: string
	@jsonProperty()
	relatedToId: string
	@jsonProperty()
	type: AuditTrailEntryType
	@jsonProperty()
	actionType?: AuditTrailActionType
	@jsonProperty()
	message: string
	@jsonProperty()
	isHtml?: boolean
	@jsonArrayProperty(() => AuditTrailEntryAttachment) // intentionally not using AuditTrailEntryAttachmentData here as we can't override this property in the derived class
	attachments?: AuditTrailEntryAttachmentData[]

	constructor(init: Partial<AuditTrailEntryData>) {
		assignCommonProperties(this, init)
	}

	get actionName(): string | undefined {
		return this.actionType
			? AuditTrailActionTypes[
					this.actionType as keyof typeof AuditTrailActionTypes
				]?.name
			: this.actionType
	}
	get actionIcon(): string | undefined {
		return this.actionType
			? AuditTrailActionTypes[
					this.actionType as keyof typeof AuditTrailActionTypes
				]?.icon
			: undefined
	}
}

@jsonClass('AuditTrailEntry')
export class AuditTrailEntry
	extends AuditTrailEntryData
	implements ChatMessage
{
	@jsonProperty()
	id: number
	@jsonProperty(AnyType)
	userIdAndName: UserIdAndName
	@jsonProperty(Date)
	timestamp: Date
	declare attachments: AuditTrailEntryAttachment[]

	constructor(init: Partial<AuditTrailEntry>) {
		super(init)
		assignCommonProperties(this, init)
	}
}
